import "./AnimalsCard.css";
import type { AnimalRecord } from "../../api/animals";

function escapeHtml(value: unknown): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function focalPercent(value: number | null | undefined): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return 50;
  return Math.max(0, Math.min(100, value * 100));
}

function renderImages(animal: AnimalRecord): string {
  const images = Array.isArray(animal.images) ? animal.images.slice(0, 3) : [];
  if (!images.length) return "";

  return `
    <div class="animal-card-gallery animal-card-gallery-${images.length}">
      ${images
        .map((image, index) => {
          const src = image.thumbnail_url || image.image_url;
          const alt = image.alt_text || `${animal.common_name} image ${index + 1}`;
          return `
            <figure class="animal-card-image-slot animal-card-image-${index + 1}">
              <img
                src="${escapeHtml(src)}"
                alt="${escapeHtml(alt)}"
                loading="${index === 0 ? "eager" : "lazy"}"
                referrerpolicy="no-referrer"
                style="object-position:${focalPercent(image.focal_x)}% ${focalPercent(image.focal_y)}%;"
              />
            </figure>
          `;
        })
        .join("")}
    </div>
  `;
}

function renderFacts(animal: AnimalRecord): string {
  const facts = [
    ["Habitat", animal.habitat],
    ["Diet", animal.diet],
    ["Lifespan", animal.lifespan],
    ["Status", animal.conservation_status],
    ["Weight", animal.weight],
    ["Length", animal.length],
    ["Range", animal.geographic_range],
  ].filter(([, value]) => String(value || "").trim());

  if (!facts.length) return "";

  return `
    <dl class="animal-card-facts">
      ${facts
        .map(
          ([label, value]) => `
            <div class="animal-card-fact">
              <dt>${escapeHtml(label)}</dt>
              <dd>${escapeHtml(value)}</dd>
            </div>
          `,
        )
        .join("")}
    </dl>
  `;
}

function renderSources(animal: AnimalRecord): string {
  if (!Array.isArray(animal.sources) || !animal.sources.length) return "";

  return `
    <div class="animal-card-sources">
      ${animal.sources
        .map(
          (source) => `
            <a
              href="${escapeHtml(source.url)}"
              target="_blank"
              rel="noopener noreferrer"
            >${escapeHtml(source.name)}</a>
          `,
        )
        .join("")}
    </div>
  `;
}

export function renderAnimalsCard(animal: AnimalRecord): string {
  const subtitle = [animal.scientific_name, animal.animal_type]
    .filter((value) => String(value || "").trim())
    .join(" · ");

  return `
    <article class="animal-card" data-animal-slug="${escapeHtml(animal.slug)}">
      <header class="animal-card-header">
        <div>
          <div class="animal-card-kicker">AYESEARCH ANIMALS</div>
          <h1>${escapeHtml(animal.common_name)}</h1>
          ${subtitle ? `<p class="animal-card-subtitle">${escapeHtml(subtitle)}</p>` : ""}
        </div>
      </header>

      ${renderImages(animal)}

      ${
        animal.summary
          ? `<p class="animal-card-summary">${escapeHtml(animal.summary)}</p>`
          : ""
      }

      ${renderFacts(animal)}
      ${renderSources(animal)}
    </article>
  `;
}
